import React, { useState, useCallback, useEffect } from "react";
import { Button } from "../../../../../components/ui/button";
import { Loader2 } from "lucide-react";

const PODetailsForm = ({
  initialPoDetails = {},
  onCreate,
  creatingPO,
  onBack,
  mantisBusiness = null,
}) => {
  // Internal state management to prevent focus loss
  const [poDetails, setPoDetails] = useState({
    payment_terms: "",
    shipping_terms: "",
    notes: "",
    address: "",
    city: "",
    zip_code: "",
    shipping_address: "",
    tax_rate: "",
    shipping_cost: "",
    other_costs: "",
    discount_percentage: "",
  });

  // Only initialize once on mount, not on every prop change
  useEffect(() => {
    // Only set initial values if they exist and we haven't initialized yet
    if (Object.keys(initialPoDetails).length > 0) {
      setPoDetails({
        payment_terms: initialPoDetails.payment_terms || "",
        shipping_terms: initialPoDetails.shipping_terms || "",
        notes: initialPoDetails.notes || "",
        address: initialPoDetails.address || mantisBusiness?.address || "",
        city:
          initialPoDetails.city ||
          (mantisBusiness?.cities && mantisBusiness.cities.length > 0
            ? mantisBusiness.cities[0]
            : "") ||
          "",
        zip_code: initialPoDetails.zip_code || "",
        shipping_address:
          initialPoDetails.shipping_address || mantisBusiness?.address || "",
        tax_rate: initialPoDetails.tax_rate || "",
        shipping_cost: initialPoDetails.shipping_cost || "",
        other_costs: initialPoDetails.other_costs || "",
        discount_percentage: initialPoDetails.discount_percentage || "",
      });
    } else if (mantisBusiness) {
      // If no initialPoDetails but mantisBusiness exists, prefill with business data
      setPoDetails((prev) => ({
        ...prev,
        address: mantisBusiness.address || "",
        city:
          mantisBusiness.cities && mantisBusiness.cities.length > 0
            ? mantisBusiness.cities[0]
            : "",
        shipping_address: mantisBusiness.address || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusiness]); // Add mantisBusiness as dependency

  const handleInputChange = useCallback((field, value) => {
    setPoDetails((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    // Pass the form data back to parent
    onCreate(poDetails);
  }, [poDetails, onCreate]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
        Complete los detalles adicionales para la orden de compra:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Payment Terms */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Términos de Pago
          </label>
          <input
            type="text"
            value={poDetails.payment_terms}
            onChange={(e) => handleInputChange("payment_terms", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Ej: Net 30 días"
          />
        </div>

        {/* Shipping Terms */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Términos de Envío
          </label>
          <input
            type="text"
            value={poDetails.shipping_terms}
            onChange={(e) =>
              handleInputChange("shipping_terms", e.target.value)
            }
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Ej: FOB Origen"
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Dirección
          </label>
          <input
            type="text"
            value={poDetails.address}
            onChange={(e) => handleInputChange("address", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Dirección de facturación"
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Ciudad <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={poDetails.city}
            onChange={(e) => handleInputChange("city", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Ciudad"
            required
          />
        </div>

        {/* Zip Code */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Código Postal
          </label>
          <input
            type="text"
            value={poDetails.zip_code}
            onChange={(e) => handleInputChange("zip_code", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Código postal"
          />
        </div>

        {/* Shipping Address */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Dirección de Envío <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={poDetails.shipping_address}
            onChange={(e) =>
              handleInputChange("shipping_address", e.target.value)
            }
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Dirección de envío"
            required
          />
        </div>

        {/* Tax Rate */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Tasa de Impuesto %
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={poDetails.tax_rate}
            onChange={(e) => handleInputChange("tax_rate", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="0.00"
          />
        </div>

        {/* Shipping Cost */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Costo de Envío
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={poDetails.shipping_cost}
            onChange={(e) => handleInputChange("shipping_cost", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="0.00"
          />
        </div>

        {/* Other Costs */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Otros Costos
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={poDetails.other_costs}
            onChange={(e) => handleInputChange("other_costs", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="0.00"
          />
        </div>

        {/* Discount Percentage */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Porcentaje de Descuento %
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={poDetails.discount_percentage}
            onChange={(e) =>
              handleInputChange("discount_percentage", e.target.value)
            }
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Notas
        </label>
        <textarea
          value={poDetails.notes}
          onChange={(e) => handleInputChange("notes", e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          placeholder="Notas adicionales para la orden de compra..."
        />
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button variant="outline" onClick={onBack} disabled={creatingPO}>
          Volver
        </Button>
        <Button
          variant="default"
          onClick={handleSubmit}
          disabled={
            creatingPO || !poDetails.city || !poDetails.shipping_address
          }
          className="text-white"
        >
          {creatingPO ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Creando...
            </>
          ) : (
            "Crear Orden de Compra"
          )}
        </Button>
      </div>
    </div>
  );
};

export default PODetailsForm;
